import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Grid,
  GridItem,
  Image,
  Box,
  Spinner,
  Center,
  useToast,
  Text,
  Input,
  Spacer,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { imageClient } from "../../../../../Api/BackendClient2";
import { Trash, Upload } from "lucide-react";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  prefix: string; // e.g. "sponsor_"
  onSelect: (imageUrl: string) => void;
};

export const ImagePickerModal: React.FC<Props> = ({
  isOpen,
  onClose,
  prefix,
  onSelect,
}) => {
  const [images, setImages] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Upload flow
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [filenameInput, setFilenameInput] = useState("");
  const [isNaming, setIsNaming] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const toast = useToast();

  // -------------------------
  // Helpers
  // -------------------------
  const sanitizeFilename = (name: string) => {
    return name
      .toLowerCase()
      .replace(/\.[^/.]+$/, "") // remove extension
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-_]/g, "")
      .replace(/-+/g, "-");
  };

  const getExtension = (file: File) => {
    return file.name.split(".").pop() || "jpg";
  };

  // -------------------------
  // Load images
  // -------------------------
  const loadImages = async () => {
    try {
      setLoading(true);
      const data = await imageClient.getImages(`${prefix}*`);
      setImages(data);
    } catch (err: any) {
      toast({
        status: "error",
        title: "Failed to load images",
        description: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadImages();
      setSelected(null);
    }
  }, [isOpen]);

  // -------------------------
  // Upload flow
  // -------------------------
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const cleanName = sanitizeFilename(file.name);

    setPendingFile(file);
    setFilenameInput(cleanName);
    setIsNaming(true);

    // reset so same file can be picked again
    e.target.value = "";
  };

  const handleConfirmUpload = async () => {
    if (!pendingFile) return;

    try {
      const ext = getExtension(pendingFile);
      const filename = `${prefix}${sanitizeFilename(filenameInput)}.${ext}`;

      await imageClient.uploadImage(filename, pendingFile);

      await loadImages();
      setSelected(filename);

      toast({
        status: "success",
        title: "Bild uppladdad",
      });

      const url = imageClient.getImageUrl(filename);
      onSelect(url);
      onClose();

      // reset
      setPendingFile(null);
      setIsNaming(false);
    } catch (err: any) {
      toast({
        status: "error",
        title: "Upload failed",
        description: err.message,
      });
    }
  };

  // -------------------------
  // Delete
  // -------------------------
  const handleDelete = async () => {
    if (!selected) return;

    try {
      await imageClient.deleteImage(selected);

      setImages((prev) => prev.filter((img) => img !== selected));
      setSelected(null);

      toast({
        status: "success",
        title: "Image deleted",
      });
    } catch (err: any) {
      toast({
        status: "error",
        title: "Delete failed",
        description: err.message,
      });
    }
  };

  // -------------------------
  // Select
  // -------------------------
  const handleSelect = () => {
    if (!selected) return;

    const url = imageClient.getImageUrl(selected);
    onSelect(url);
    onClose();
  };

  // -------------------------
  // UI
  // -------------------------
  return (
    <>
      {/* MAIN MODAL */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="4xl"
        scrollBehavior="inside"
        blockScrollOnMount
        isCentered
       >
        <ModalOverlay />
        <ModalContent
         h="95vh"
    maxH="95vh"
    display="flex"
    flexDirection="column"
        >
          <ModalHeader>Välj bild</ModalHeader>
          <ModalCloseButton />

          <ModalBody
          flex="1" overflow="hidden" display="flex" flexDirection="column"
          >
            {loading ? (
              <Center py={10}>
                <Spinner />
              </Center>
            ) : images.length === 0 ? (
              <Center py={10}>
                <Text>Hittade inga bilder</Text>
              </Center>
            ) : (
              <Box flex="1" overflowY="auto"
              onClick={() => setSelected(null)}
              >
                <Grid
                  templateColumns="repeat(auto-fill, minmax(150px, 1fr))"
                  gap={4}
                >
                  {images.map((img) => {
                    const url = imageClient.getImageUrl(img);
                    const isSelected = selected === img;

                    return (
                      <GridItem key={img}>
                        <Box
                          borderWidth={isSelected ? "3px" : "1px"}
                          borderColor={
                            isSelected ? "blue.400" : "gray.200"
                          }
                          borderRadius="md"
                          overflow="hidden"
                          cursor="pointer"
                          onClick={(e) => 
                          {
                            e.stopPropagation();
                            setSelected((prev) => (prev === img ? null : img))}
                          }
                          _hover={{ opacity: 0.85 }}
                        >
                          <Tooltip label={url} >
                          <Image
                            src={url}
                            alt={img}
                            objectFit="contain"
                            boxSize="150px"
                            mx="auto"
                          />
                          </Tooltip>
                        </Box>
                      </GridItem>
                    );
                  })}
                </Grid>
              </Box>
            )}

            {/* Hidden file input */}
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileSelected}
            />
          </ModalBody>

          <ModalFooter gap={3}>
            <Tooltip label={"Delete image"}>
            <IconButton
              colorScheme="red"
              onClick={handleDelete}
              isDisabled={!selected}
              aria-label="delete image"
            >
              <Trash/>
            </IconButton>
            </Tooltip>


            <Tooltip label={"Ladda upp ny bild"}>
            <IconButton colorScheme="green" onClick={handleUploadClick} aria-label="upload image" isDisabled={!!selected} disabled={!!selected}>
              <Upload />
            </IconButton>
            </Tooltip>
            <Spacer/>

            <Button
              colorScheme="blue"
              onClick={handleSelect}
              isDisabled={!selected}
            >
              Välj bild
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* NAMING MODAL */}
      <Modal
        isOpen={isNaming}
        onClose={() => setIsNaming(false)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Spara bild som...</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Text mb={2}>Filnamn</Text>

            <Input
              value={filenameInput}
              onChange={(e) => setFilenameInput(e.target.value)}
              autoFocus
            />

            {pendingFile && (
              <Text mt={2} fontSize="sm" color="gray.500">
                Sparas som: {prefix}
                {sanitizeFilename(filenameInput)}.{getExtension(pendingFile)}
              </Text>
            )}
          </ModalBody>

          <ModalFooter>
            <Button mr={3} onClick={() => setIsNaming(false)}>
              Avbryt
            </Button>

            <Button
              colorScheme="blue"
              onClick={handleConfirmUpload}
              isDisabled={!sanitizeFilename(filenameInput)}
            >
              Ladda upp
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};